import React from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import moment from 'moment'
import { BASE_URL, NOMBRE_SITIO, NOMBRE_PAIS, OG_ID_BY_SITE, ID_PAIS, SITE_ID } from '../../../../constants'
import hrefLangs from '../../../../constants/href-langs'
import features from '../../../../constants/features-per-country'
import { getCompanyURL } from '../../../../hooks/get-company-url'
import { companyData } from '../../../../constants/ficha-aviso-metas'

const meses = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
]

const cutFechaPublicacion = fechaPublicacion => {
  const [dia, mes, anio] = fechaPublicacion.split('-')
  return { dia, mes, anio }
}

const monthNumToName = monthNum => {
  return meses[monthNum - 1] || ''
}

const makeGoogleForJobsJSON = aviso => {
  const fechaPublicacionISO = aviso.fechaPublicacion ? moment(aviso.fechaPublicacion, 'DD-MM-YYYY').toISOString() : ''
  const fechaFinalizacionISO = aviso.fechaFinalizacion
    ? moment(aviso.fechaFinalizacion, 'DD-MM-YYYY').toISOString()
    : ''
  const descripcionSEO = aviso.descripcion.replace(/\\"/, "\\'")
  const empresaSEO = aviso.empresa.denominacion || 'Confidencial'

  const data = {
    title: aviso.titulo,
    description: descripcionSEO,
    datePosted: fechaPublicacionISO,
    validThrough: fechaFinalizacionISO,
    employmentType: aviso.tipoTrabajo.nombre,
    jobLocation: {
      address: {
        addressLocality: aviso.localizacion.localidadDetalle,
        addressRegion: aviso.localizacion.provinciaDetalle,
        '@type': 'PostalAddress',
      },
      '@type': 'Place',
    },
    hiringOrganization: empresaSEO,
    '@type': 'JobPosting',
    '@context': 'http://schema.org',
  }

  return JSON.stringify(data)
}

const googleAdsEnabled = features[SITE_ID || 'BMAR'].googleAds?.ficha?.enabled

const Metas = ({ breadcrumbs, activeAviso, reviews }) => {
  const { t } = useTranslation()
  const avisoFinalizado = activeAviso && (activeAviso.estado === 'vencido' || activeAviso.estado === 'offline')
  const paisId = activeAviso && activeAviso.localizacion.paisId
  const isAvisoSinFronteras = paisId !== ID_PAIS
  const settingsCompanyURL = activeAviso && activeAviso.empresa && getCompanyURL({ company: activeAviso?.empresa })

  let mesName
  let anio
  if (activeAviso) {
    const { mes: mesNum, anio: anioNum } = cutFechaPublicacion(activeAviso.fechaPublicacion)
    mesName = monthNumToName(mesNum)
    anio = anioNum
  }

  const wordingData = {
    countryName: NOMBRE_PAIS,
    siteName: NOMBRE_SITIO,
    title: activeAviso && activeAviso.titulo,
    company:
      activeAviso &&
      activeAviso.empresa &&
      activeAviso.empresa.denominacion &&
      ` en ${activeAviso.empresa.denominacion}`,
    mes: mesName,
    anio,
  }
  const pageTitle = t(`seoWordings.ficha_aviso.title`, wordingData)
  const pageDescription = t(`seoWordings.ficha_aviso.description`, wordingData)
  const ulrCanonical = `${BASE_URL}${activeAviso?.seoFriendlyUrl}`

  return activeAviso ? (
    <Helmet>
      <meta property="og:title" content={pageTitle} />
      <title>{pageTitle}</title>
      <meta name="description" property="og:description" content={pageDescription} />
      <meta property="og:site_name" content={NOMBRE_SITIO} />
      <meta property="og:url" content={ulrCanonical} />
      <meta property="fb:app_id" content={OG_ID_BY_SITE[NOMBRE_SITIO]} />

      <link rel="canonical" href={ulrCanonical} />

      {isAvisoSinFronteras &&
        hrefLangs.map(item => (
          <link
            key={`link-${item.country}`}
            rel="alternate"
            hrefLang={item.country === 'x-default' ? item.country : `es-${item.country}`}
            href={`${item.href}${activeAviso.seoFriendlyUrl}`}
          />
        ))}

      {!avisoFinalizado && <script type="application/ld+json">{makeGoogleForJobsJSON(activeAviso)}</script>}
      {breadcrumbs && <script type="application/ld+json">{breadcrumbs.json}</script>}

      {googleAdsEnabled && (
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6635751356116235"
          crossOrigin="anonymous"
        ></script>
      )}

      {activeAviso && reviews && (
        <script type="application/ld+json">{companyData(activeAviso, reviews, settingsCompanyURL)}</script>
      )}
    </Helmet>
  ) : null
}

const states = ({ activeAvisoStore, breadcrumbsStore, reviewStore }) => ({
  breadcrumbs: breadcrumbsStore.breadcrumbs,
  activeAviso: activeAvisoStore.aviso,
  reviews: reviewStore.reviews,
})

export default connect(states)(Metas)
